import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { 
  ShoppingCart, Download, ChevronLeft, Database, Cpu, Car, 
  FileText, Loader2, Check, CreditCard, Mail, X
} from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import { useCanonical } from '../hooks/useCanonical';
import Footer from '../components/Footer';

const API_URL = BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function FileStoreProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  
  // Cart & Checkout
  const [inCart, setInCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Manage canonical tag directly in DOM - only for valid products
  useEffect(() => {
    if (slug && product) {
      const productSlug = product.slug || slug;
      const canonicalUrl = `https://ecuflashservice.com/file-store/product/${productSlug}`;
      
      // Remove any existing canonical link
      const existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
      
      // Add new canonical link
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);

      // Inject Product schema directly (Helmet doesn't handle script tags reliably)
      const seoData = product.seo_content || {};
      const desc = seoData.meta_description || `Buy ${product.brand} ${product.name} ECU file. Instant download after payment.`;
      const seoTitle = seoData.title || `${product.brand} ${product.name} ECU File | Download $${product.price}`;
      const priceValid = new Date();
      priceValid.setFullYear(priceValid.getFullYear() + 1);

      const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": `${product.brand} ${product.name} ECU File`,
        "description": desc,
        "image": "https://ecuflashservice.com/favicon-512.png",
        "brand": { "@type": "Brand", "name": product.brand },
        "category": "ECU Tuning Files",
        "sku": product.product_id,
        "mpn": product.ecu_part_number || product.product_id,
        "offers": {
          "@type": "Offer",
          "url": canonicalUrl,
          "priceCurrency": "USD",
          "price": product.price,
          "priceValidUntil": priceValid.toISOString().split('T')[0],
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "Organization", "name": "ECU Flash Service" }
        }
      };

      const schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.id = 'product-schema';
      schemaScript.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaScript);

      // Override description meta
      let descMeta = document.querySelector('meta[name="description"]');
      const origDesc = descMeta ? descMeta.content : '';
      if (descMeta) descMeta.content = desc;

      // Set robots meta - noindex to prevent Google from indexing product pages
      let robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        robotsMeta.content = 'noindex, nofollow';
        document.head.appendChild(robotsMeta);
      } else {
        robotsMeta.content = 'noindex, nofollow';
      }

      // Set keywords meta from seo_content
      const keywords = seoData.keywords || [];
      let keywordsMeta = document.querySelector('meta[name="keywords"]');
      const origKeywords = keywordsMeta ? keywordsMeta.content : '';
      if (keywords.length > 0) {
        if (!keywordsMeta) {
          keywordsMeta = document.createElement('meta');
          keywordsMeta.name = 'keywords';
          document.head.appendChild(keywordsMeta);
        }
        keywordsMeta.content = keywords.join(', ');
      }

      // Set title
      document.title = seoTitle;
      
      // Cleanup on unmount
      return () => {
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.remove();
        const schemaEl = document.getElementById('product-schema');
        if (schemaEl) schemaEl.remove();
        if (descMeta) descMeta.content = origDesc;
        if (keywordsMeta) keywordsMeta.content = origKeywords;
      };
    }
  }, [slug, product]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        // Fetch product by slug (or product_id for backward compat)
        const response = await fetch(`${API_URL}/api/file-store/product/${slug}`);
        if (!response.ok) throw new Error('Product not found');
        
        const product = await response.json();
        setProduct(product);
        
        // Fetch related products (same brand)
        const brand = product.brand;
        const relatedRes = await fetch(`${API_URL}/api/file-store/products?brand=${encodeURIComponent(brand)}&page_size=6`);
        if (relatedRes.ok) {
          const relatedData = await relatedRes.json();
          setRelatedProducts(relatedData.products.filter(p => p.product_id !== product.product_id).slice(0, 5));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleBuyNow = async () => {
    if (!customerEmail) {
      alert('Please enter your email address');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/file-store/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            file_id: product.product_id,
            name: product.name,
            price: product.price,
            file_type: product.file_type,
            bucket: product.bucket,
            key: product.key
          }],
          email: customerEmail
        })
      });
      
      if (!response.ok) throw new Error('Failed to create order');
      
      const data = await response.json();
      setOrderId(data.order_id);
    } catch (err) {
      alert('Failed to create order: ' + err.message);
    }
  };

  const createPayPalOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [{
        amount: {
          value: product.price.toFixed(2),
          currency_code: 'USD'
        },
        description: `ECU File: ${product.name}`
      }]
    });
  };

  const onPayPalApprove = async (data, actions) => {
    setProcessingPayment(true);
    
    try {
      const details = await actions.order.capture();
      
      const response = await fetch(
        `${API_URL}/api/file-store/complete-payment/${orderId}?payment_id=${details.id}&payment_method=paypal`,
        { method: 'POST' }
      );
      
      if (!response.ok) throw new Error('Failed to complete payment');
      
      const result = await response.json();
      setDownloadLinks(result.downloads);
      setOrderComplete(true);
    } catch (err) {
      alert('Payment processing failed: ' + err.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error || !product) {
    // Imperatively set noindex for crawlers (Helmet doesn't reliably inject meta tags here)
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'noindex, nofollow';
    let descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.content = 'The requested ECU file product was not found.';
    document.title = 'Product Not Found | ECU Flash Service';

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-white mb-4">Product Not Found</h1>
        <p className="text-gray-400 mb-4">The ECU file you're looking for doesn't exist or has been removed.</p>
        <Link to="/file-store" className="text-cyan-400 hover:text-cyan-300">
          ← Back to File Store
        </Link>
      </div>
    );
  }

  // Generate SEO-friendly title and description

  // Use enriched content from backend API
  const seo = product.seo_content || {};
  const contentBlock = {
    heading: seo.heading || `${product.brand} ECU Calibration File`,
    paragraphs: seo.paragraphs || [],
  };
  const seoVehicle = seo.vehicle || product.brand;
  const seoEngine = seo.engine || '';
  const seoEcu = seo.ecu_info || '';
  const seoParsed = seo.parsed || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">

      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-cyan-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/file-store" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <ChevronLeft className="h-5 w-5" />
              Back to File Store
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              Main Site
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm mb-6">
          <ol className="flex items-center gap-2 text-gray-400">
            <li><Link to="/file-store" className="hover:text-cyan-400">File Store</Link></li>
            <li>/</li>
            <li><Link to={`/file-store?brand=${encodeURIComponent(product.brand)}`} className="hover:text-cyan-400">{product.brand}</Link></li>
            <li>/</li>
            <li className="text-white truncate max-w-xs">{product.name}</li>
          </ol>
        </nav>

        {/* Product Details */}
        <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden">
          <div className="p-6 md:p-8">
            {/* Header */}
            <div className="flex flex-wrap items-start gap-4 mb-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded text-sm font-medium ${
                    product.file_type === 'modified' 
                      ? 'bg-purple-500/20 text-purple-400' 
                      : 'bg-green-500/20 text-green-400'
                  }`}>
                    {product.file_type === 'modified' ? 'Modified File' : 'Stock File'}
                  </span>
                  {product.tuning_stage && (
                    <span className="px-3 py-1 rounded text-sm font-medium bg-cyan-500/20 text-cyan-400">
                      {product.tuning_stage}
                    </span>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{product.name}</h1>
                <p className="text-gray-400 flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  {product.brand}
                </p>
              </div>
              
              <div className="text-right">
                <p className={`text-4xl font-bold ${
                  product.file_type === 'modified' ? 'text-purple-400' : 'text-green-400'
                }`}>
                  ${product.price.toFixed(2)}
                </p>
                <p className="text-gray-500 text-sm">USD</p>
              </div>
            </div>

            {/* Specs Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gray-900/50 rounded-lg p-4">
                <p className="text-gray-500 text-sm">File Size</p>
                <p className="text-white font-medium">{formatSize(product.size)}</p>
              </div>
              {product.ecu_type && (
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">ECU Type</p>
                  <p className="text-white font-medium">{product.ecu_type}</p>
                </div>
              )}
              {product.ecu_part_number && (
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Part Number</p>
                  <p className="text-white font-medium">{product.ecu_part_number}</p>
                </div>
              )}
              {product.ecu_manufacturer && (
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Manufacturer</p>
                  <p className="text-white font-medium">{product.ecu_manufacturer}</p>
                </div>
              )}
              {product.toyota_part_number && (
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Toyota Part #</p>
                  <p className="text-white font-medium">{product.toyota_part_number}</p>
                </div>
              )}
              {product.fuel_type && (
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <p className="text-gray-500 text-sm">Fuel Type</p>
                  <p className="text-white font-medium">{product.fuel_type}</p>
                </div>
              )}
            </div>

            {/* Buy Button */}
            <button
              onClick={() => setShowCheckout(true)}
              className="w-full py-4 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="buy-now-button"
            >
              <ShoppingCart className="h-5 w-5" />
              Buy Now - ${product.price.toFixed(2)}
            </button>
          </div>
        </div>

        {/* Rich Content Section for SEO */}
        <div className="mt-8 bg-gray-800/30 rounded-2xl border border-gray-700/30 p-6 md:p-8">
          <h2 className="text-xl font-bold text-white mb-4">{contentBlock.heading}</h2>
          {contentBlock.paragraphs.map((p, i) => (
            <p key={i} className="text-gray-400 leading-relaxed mb-4 last:mb-0">{p}</p>
          ))}

          {/* Vehicle & File Specs */}
          <div className="mt-6 pt-6 border-t border-gray-700/30">
            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">File Details</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <div className="flex justify-between sm:block">
                <dt className="text-gray-500">Vehicle</dt>
                <dd className="text-white">{seoVehicle}</dd>
              </div>
              {seoEngine && (
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">Engine</dt>
                  <dd className="text-white">{seoEngine}</dd>
                </div>
              )}
              {seoParsed.year && (
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">Year</dt>
                  <dd className="text-white">{seoParsed.year}</dd>
                </div>
              )}
              {seoParsed.fuel_type && (
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">Fuel Type</dt>
                  <dd className="text-white">{seoParsed.fuel_type}</dd>
                </div>
              )}
              <div className="flex justify-between sm:block">
                <dt className="text-gray-500">File Type</dt>
                <dd className="text-white">{product.file_type === 'modified' ? 'Modified / Remapped' : 'Original / Stock'}</dd>
              </div>
              {seoEcu && (
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">ECU</dt>
                  <dd className="text-white">{seoEcu}</dd>
                </div>
              )}
              {product.ecu_part_number && (
                <div className="flex justify-between sm:block">
                  <dt className="text-gray-500">ECU Part Number</dt>
                  <dd className="text-white">{product.ecu_part_number}</dd>
                </div>
              )}
              <div className="flex justify-between sm:block">
                <dt className="text-gray-500">File Size</dt>
                <dd className="text-white">{formatSize(product.size)}</dd>
              </div>
              <div className="flex justify-between sm:block">
                <dt className="text-gray-500">Delivery</dt>
                <dd className="text-white">Instant Download</dd>
              </div>
            </dl>
          </div>

          {/* SEO Keywords (hidden, for crawlers) */}
          {seo.keywords && seo.keywords.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {seo.keywords.slice(0, 6).map((kw, i) => (
                <span key={i} className="px-2 py-1 bg-gray-700/30 text-gray-500 text-xs rounded">
                  {kw}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-white mb-6">More {product.brand} Files</h2>
            <div className="grid gap-4">
              {relatedProducts.map(related => (
                <Link
                  key={related.product_id}
                  to={`/file-store/product/${related.slug || related.product_id}`}
                  className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 hover:border-cyan-500/50 transition-colors flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        related.file_type === 'modified' 
                          ? 'bg-purple-500/20 text-purple-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {related.file_type}
                      </span>
                    </div>
                    <p className="text-white truncate">{related.name}</p>
                  </div>
                  <span className={`text-lg font-bold ${
                    related.file_type === 'modified' ? 'text-purple-400' : 'text-green-400'
                  }`}>
                    ${related.price.toFixed(2)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => !processingPayment && !orderComplete && setShowCheckout(false)} />
          
          <div className="relative w-full max-w-lg bg-gray-900 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {orderComplete ? 'Download Ready!' : 'Complete Purchase'}
              </h2>
              {!processingPayment && !orderComplete && (
                <button
                  onClick={() => setShowCheckout(false)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              )}
            </div>
            
            <div className="p-6">
              {orderComplete ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                  <p className="text-gray-400 mb-6">Your download is ready.</p>
                  
                  <div className="space-y-2 mb-6">
                    {downloadLinks.map((link, idx) => (
                      <a
                        key={idx}
                        href={`${API_URL}${link.download_url}`}
                        className="block p-4 bg-cyan-500 hover:bg-cyan-600 text-white font-medium rounded-lg transition-colors"
                      >
                        <Download className="h-5 w-5 inline mr-2" />
                        Download File
                      </a>
                    ))}
                  </div>
                  
                  <p className="text-gray-500 text-sm">
                    Link expires in 72 hours. Max 5 downloads.
                  </p>
                  
                  <button
                    onClick={() => {
                      setShowCheckout(false);
                      setOrderComplete(false);
                      setOrderId(null);
                    }}
                    className="mt-4 text-cyan-400 hover:text-cyan-300"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : !orderId ? (
                <div>
                  <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                    <p className="text-white font-medium truncate">{product.name}</p>
                    <p className="text-gray-500 text-sm">{product.brand} • {product.file_type}</p>
                    <p className="text-2xl font-bold text-cyan-400 mt-2">${product.price.toFixed(2)}</p>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-gray-400 text-sm mb-2">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                      <input
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 outline-none"
                      />
                    </div>
                    <p className="text-gray-500 text-xs mt-2">Download link will be sent here</p>
                  </div>
                  
                  <button
                    onClick={handleBuyNow}
                    disabled={!customerEmail}
                    className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                  >
                    Continue to Payment
                  </button>
                </div>
              ) : (
                <div>
                  <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
                    <p className="text-gray-400 text-sm mb-1">Order #{orderId}</p>
                    <p className="text-2xl font-bold text-white">${product.price.toFixed(2)}</p>
                  </div>
                  
                  {processingPayment ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-4" />
                      <p className="text-gray-400">Processing payment...</p>
                    </div>
                  ) : (
                    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD' }}>
                      <PayPalButtons
                        style={{ layout: 'vertical', color: 'blue' }}
                        createOrder={createPayPalOrder}
                        onApprove={onPayPalApprove}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          alert('Payment failed. Please try again.');
                        }}
                      />
                    </PayPalScriptProvider>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
